
import { Component, Vue } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import UnapprovedPOsModule from '@/store/modules/unapproved-pos';
import PODataTable from '@/components/purchase-order/unapproved/PODataTable.vue';
import PODetailModal from '@/components/purchase-order/unapproved/PODetailModal.vue';
import { UnapprovedPO, Supplier, AutomationConfig } from '@/models/unapprovedPO';
import { SupplierList } from '@/collections/suppliers';
import { AutomationConfigList } from '@/collections/automation_configs';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import dateFormat from 'dateformat';

@Component({
  components: {
    'po-data-table': PODataTable,
    'po-detail-modal': PODetailModal,
  },
})
export default class UnapprovedPOs extends Vue {
  private unapprovedPOsStore = getModule(UnapprovedPOsModule, this.$store);
  private localSuppliers: Supplier[] = [];
  private localAutomationConfigs: AutomationConfig[] = [];
  private loadingSuppliers = false;
  private loadingConfigs = false;
  private debounceTimer: number | null = null;

  // Filter panel state
  private filterPanelExpanded: number | undefined = 0;

  // Detail modal state
  private detailModalVisible = false;
  private selectedPoId: number | null = null;
  private selectedPoData: any = null;

  private headers = [
    { text: 'PO Number', value: 'poNumber', sortable: false },
    { text: 'Supplier', value: 'supplierName', sortable: false },
    { text: 'Total Amount', value: 'totalAmount', sortable: false, align: 'right' },
    { text: 'Created', value: 'createdAt', sortable: false },
    { text: 'Automation', value: 'automationConfigName', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
  ];

  filters: {
    supplierId: number | null;
    automationConfigId: number | null;
    searchQuery: string;
  } = {
    supplierId: null,
    automationConfigId: null,
    searchQuery: '',
  };

  get purchaseOrders(): UnapprovedPO[] {
    const rawPurchaseOrders = this.unapprovedPOsStore.purchaseOrders || [];
    return rawPurchaseOrders.map((po: any) => {
      const createdDate = new Date(po.createdAt);
      const currentDate = new Date();
      const daysSinceGeneration = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

      return {
        id: po.id,
        poNumber: po.poNumber || po.name || `PO-${po.id}`,
        supplierId: po.supplierId || po.supplier?.id || po.supplier_id,
        supplierName: po.supplier?.name || po.supplierName || 'Unknown Supplier',
        totalAmount: po.totalAmount || po.total || 0,
        currencyCode: po.currencyCode || po.currency_code || null,
        itemCount: po.itemCount || po.items?.length || po.orderData?.length || 0,
        status: po.status || 'pending_approval',
        createdAt: po.createdAt,
        automationConfigId: po.automationConfigId || po.automationConfig?.id || po.automation_config_id,
        automationConfigName: po.automationConfig?.name || po.automationConfigName || 'Manual',
        daysSinceGeneration: daysSinceGeneration,
      };
    });
  }

  get loading(): boolean {
    return this.unapprovedPOsStore.loading;
  }

  get suppliers(): Supplier[] {
    return this.unapprovedPOsStore.metadata?.suppliers?.length > 0
      ? this.unapprovedPOsStore.metadata.suppliers
      : this.localSuppliers;
  }

  get automationConfigs(): AutomationConfig[] {
    return this.unapprovedPOsStore.metadata?.automationConfigs?.length > 0
      ? this.unapprovedPOsStore.metadata.automationConfigs
      : this.localAutomationConfigs;
  }

  get totalItems(): number {
    return this.unapprovedPOsStore.pagination.totalItems;
  }

  get options(): any {
    const { page, itemsPerPage } = this.unapprovedPOsStore.pagination;
    const { sortBy, sortDesc } = this.unapprovedPOsStore.sorting;
    return { page, itemsPerPage, sortBy: [sortBy], sortDesc: [sortDesc] };
  }

  set options(value: any) {
    const { page, itemsPerPage, sortBy, sortDesc } = value;
    this.unapprovedPOsStore.context.commit('SET_PAGINATION', { page, itemsPerPage });
    this.unapprovedPOsStore.context.commit('SET_SORTING', { sortBy: sortBy[0], sortDesc: sortDesc[0] });
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchQuery ||
      this.filters.supplierId ||
      this.filters.automationConfigId
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filters.searchQuery) count++;
    if (this.filters.supplierId) count++;
    if (this.filters.automationConfigId) count++;
    return count;
  }

  created() {
    this.fetchData();
    this.fetchSuppliers();
    this.fetchAutomationConfigs();
  }

  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const suppliersCollection = new SupplierList();
      await suppliersCollection.fetch();
      this.localSuppliers = suppliersCollection.items.map(supplier => ({
        id: supplier.id!,
        name: supplier.name
      }));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchAutomationConfigs() {
    this.loadingConfigs = true;
    try {
      const configsCollection = new AutomationConfigList();
      await configsCollection.fetch();
      this.localAutomationConfigs = configsCollection.items.map(config => ({
        id: config.id!,
        name: config.name
      }));
    } catch (error) {
      console.error('Error fetching automation configs:', error);
    } finally {
      this.loadingConfigs = false;
    }
  }

  private clearFilters() {
    this.filters = {
      supplierId: null,
      automationConfigId: null,
      searchQuery: '',
    };
    this.onFilterChange();
  }

  onSearchInput() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(() => {
      this.onFilterChange();
    }, 500);
  }

  onFilterChange() {
    this.unapprovedPOsStore.context.commit('SET_FILTERS', this.filters);
    this.options = { ...this.options, page: 1 };
    this.fetchData();
  }

  fetchData() {
    const { page, itemsPerPage } = this.options;
    const { sortBy, sortDesc } = this.unapprovedPOsStore.sorting;

    this.unapprovedPOsStore.fetchUnapprovedPOs({
      page,
      limit: itemsPerPage,
      sortBy,
      sortOrder: sortDesc ? 'desc' : 'asc',
      supplierId: this.filters.supplierId || undefined,
      automationConfigId: this.filters.automationConfigId || undefined,
      searchQuery: this.filters.searchQuery,
    });
  }

  onViewDetails(item: UnapprovedPO) {
    this.selectedPoId = item.id;
    this.selectedPoData = item;
    this.detailModalVisible = true;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return dateFormat(new Date(dateString), 'mmm d, yyyy');
  }

  formatCurrency(value: number, currencyCode?: string | null): string {
    if (currencyCode) {
      return CurrencyHelper.formatCurrencyWithCode(value, currencyCode);
    }
    return CurrencyHelper.formatCurrency(value);
  }
}
